import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  FormGroup,
  Input,
  Label,
  Spinner,
} from 'reactstrap';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import {
  getSearchSmartBatchStatusEndpoint,
  processSearchSmartBatchEndpoint,
} from '../services/searchSmartService';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  propertyId: string;
  existingGallery?: string[];
  onGeneratedDescription: (description: string) => void;
}

export const GenerateDescriptionAIModal: React.FC<Props> = ({
  isOpen,
  toggle,
  propertyId,
  existingGallery = [],
  onGeneratedDescription,
}) => {
  const { getAuthToken } = useAuth();
  const { callEndpoint } = useCallApiAndLoad();

  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [generatedDescription, setGeneratedDescription] = useState<string>('');
  const [consolidatedTags, setConsolidatedTags] = useState<string[]>([]);
  const [includeImageProcessing, setIncludeImageProcessing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (existingGallery && existingGallery.length >= 2) {
        setSelectedUrls(existingGallery.slice(0, 5));
      } else {
        setSelectedUrls([]);
      }
    }
  }, [existingGallery, isOpen]);

  const resetState = () => {
    setIsProcessing(false);
    setErrorMsg(null);
    setGeneratedDescription('');
    setConsolidatedTags([]);
    if (existingGallery && existingGallery.length >= 2) {
      setSelectedUrls(existingGallery.slice(0, 5));
    } else {
      setSelectedUrls([]);
    }
  };

  const handleClose = () => {
    resetState();
    toggle();
  };

  const handleToggleUrl = (imgUrl: string) => {
    setSelectedUrls((prev) =>
      prev.includes(imgUrl)
        ? prev.filter((url) => url !== imgUrl)
        : [...prev, imgUrl],
    );
  };

  const waitSeconds = (seconds: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
  };

  const handleGenerateAI = useCallback(async () => {
    const currentToken = getAuthToken();
    if (!currentToken) {
      setErrorMsg(
        'Tu sesión ha expirado. Por favor inicia sesión nuevamente.',
      );
      return;
    }

    if (selectedUrls.length < 2 || selectedUrls.length > 5) {
      setErrorMsg('Por favor selecciona entre 2 y 5 imágenes.');
      return;
    }

    setErrorMsg(null);
    setIsProcessing(true);

    try {
      // Step 1: POST — Responds in <1s
      const processResponse = await callEndpoint(
        processSearchSmartBatchEndpoint(
          {
            property_id: propertyId,
            image_urls: selectedUrls,
            features: {
              property_summary: true,
              image_processing: includeImageProcessing,
            },
          },
          currentToken,
        ),
      );

      if (processResponse?.status !== 200 && processResponse?.status !== 201) {
        const message =
          processResponse?.data?.message ||
          'Error al iniciar el análisis con IA.';
        throw new Error(message);
      }

      const jobId =
        processResponse?.data?.data?.job_id ||
        processResponse?.data?.data?.id ||
        processResponse?.data?.job_id ||
        processResponse?.data?.id;

      if (!jobId) {
        throw new Error('No se pudo iniciar el proceso de IA.');
      }

      // Step 2: Initial wait before polling (35s)
      const FIRST_WAIT = 35;
      const POLL_INTERVAL = 10;
      const MAX_ATTEMPTS = 20;

      await waitSeconds(FIRST_WAIT);

      // Step 3: Poll GET endpoint
      let completed = false;

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const statusResponse = await callEndpoint(
            getSearchSmartBatchStatusEndpoint(jobId, currentToken),
          );

          if (statusResponse?.status >= 200 && statusResponse?.status < 400) {
            const jobData = statusResponse.data?.data || statusResponse.data;
            const status = jobData?.status;

            if (jobData?.property_summary || status === 'completed') {
              completed = true;
              const summary =
                jobData.property_summary ||
                jobData.description_general ||
                '';

              if (summary) {
                setGeneratedDescription(summary);
              }
              if (Array.isArray(jobData.tags)) {
                setConsolidatedTags(jobData.tags);
              } else if (Array.isArray(jobData.consolidated_tags)) {
                setConsolidatedTags(jobData.consolidated_tags);
              }
              break;
            }

            if (status === 'failed') {
              throw new Error(
                jobData.error ||
                'No se pudo generar la descripción. Intenta nuevamente.',
              );
            }
          }
        } catch (pollErr: any) {
          if (pollErr.message?.includes('falló') || pollErr.message?.includes('generar')) {
            throw pollErr;
          }
        }

        if (attempt < MAX_ATTEMPTS) {
          await waitSeconds(POLL_INTERVAL);
        }
      }

      if (!completed) {
        throw new Error(
          'El proceso tomó más tiempo de lo esperado. Por favor intenta nuevamente.',
        );
      }
    } catch (err: any) {
      setErrorMsg(
        err.message || 'Ocurrió un error al generar la descripción.',
      );
    } finally {
      setIsProcessing(false);
    }
  }, [
    propertyId,
    getAuthToken,
    includeImageProcessing,
    callEndpoint,
    selectedUrls,
  ]);

  const handleApplyDescription = () => {
    if (generatedDescription) {
      onGeneratedDescription(generatedDescription);
      handleClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Card className="shadow-sm border mb-4">
      <CardHeader className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
        <span className="h4 font-weight-bold mb-0 text-primary">
          ✨ Generar descripción con IA
        </span>
        <button
          type="button"
          className="close text-muted"
          onClick={handleClose}
          aria-label="Close"
          style={{ fontSize: '1.5rem', lineHeight: '1' }}
        >
          &times;
        </button>
      </CardHeader>
      <CardBody>
        {errorMsg && (
          <Alert color="danger" dismissible toggle={() => setErrorMsg(null)}>
            <i className="fa fa-exclamation-circle mr-2" />
            {errorMsg}
          </Alert>
        )}

        {!generatedDescription && !isProcessing && (
          <>
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-sm font-weight-bold text-dark">
                  Imágenes seleccionadas:{' '}
                  <strong
                    className={
                      selectedUrls.length < 2 || selectedUrls.length > 5
                        ? 'text-danger'
                        : 'text-primary'
                    }
                  >
                    {selectedUrls.length}
                  </strong>{' '}
                  <span className="text-muted font-weight-normal">(Mín. 2, Máx. 5)</span>
                </span>
              </div>

              <div
                className="d-flex flex-wrap gap-2 p-2 border rounded bg-light"
                style={{ maxHeight: '220px', overflowY: 'auto' }}
              >
                {existingGallery.map((imgUrl, idx) => {
                  const isSelected = selectedUrls.includes(imgUrl);
                  return (
                    <div
                      key={idx}
                      onClick={() => handleToggleUrl(imgUrl)}
                      className="position-relative mr-2 mb-2"
                      style={{ cursor: 'pointer', display: 'inline-block' }}
                    >
                      <img
                        src={imgUrl}
                        alt={`Foto ${idx + 1}`}
                        className="rounded shadow-sm"
                        style={{
                          width: '75px',
                          height: '75px',
                          objectFit: 'cover',
                          border: isSelected ? '3px solid #5e72e4' : '2px solid transparent',
                          opacity: isSelected ? 1 : 0.5,
                          transition: 'all 0.2s ease-in-out',
                        }}
                      />
                      {isSelected && (
                        <Badge
                          color="primary"
                          pill
                          className="position-absolute"
                          style={{
                            top: '-4px',
                            right: '-4px',
                            fontSize: '10px',
                            padding: '3px 6px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }}
                        >
                          ✓
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>

              {selectedUrls.length < 2 && (
                <Alert color="warning" className="py-2 text-sm mt-2 mb-0">
                  Selecciona al menos 2 imágenes para generar la descripción.
                </Alert>
              )}
            </div>

            <FormGroup check className="mt-3">
              <Label check className="text-sm font-weight-bold">
                <Input
                  type="checkbox"
                  checked={includeImageProcessing}
                  onChange={(e) => setIncludeImageProcessing(e.target.checked)}
                />{' '}
                Obtener Tags de la propiedad
              </Label>
            </FormGroup>
          </>
        )}

        {isProcessing && (
          <div className="text-center py-5">
            <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
            <h4 className="mt-4 font-weight-bold text-primary">
              Generando descripción con IA...
            </h4>
            <p className="text-muted text-sm">
              Analizando las imágenes seleccionadas. Este proceso puede tomar un momento.
            </p>
          </div>
        )}

        {generatedDescription && !isProcessing && (
          <div className="mt-2">
            <Alert color="success" className="mb-3 py-2 text-sm">
              <i className="fa fa-check-circle mr-2" />
              ¡Descripción generada exitosamente!
            </Alert>

            <FormGroup>
              <Label className="form-control-label font-weight-bold">
                Descripción Generada:
              </Label>
              <Input
                type="textarea"
                rows={6}
                value={generatedDescription}
                onChange={(e) => setGeneratedDescription(e.target.value)}
              />
            </FormGroup>

            {consolidatedTags.length > 0 && (
              <div className="mt-3">
                <Label className="form-control-label font-weight-bold d-block text-xs text-uppercase text-muted">
                  Tags detectados:
                </Label>
                <div className="d-flex flex-wrap gap-1">
                  {consolidatedTags.map((tag) => (
                    <Badge
                      key={tag}
                      color="info"
                      pill
                      className="mr-1 mb-1 px-2 py-1 text-lowercase"
                    >
                      #{tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardBody>
      <CardFooter className="bg-white border-top d-flex justify-content-end">
        {!generatedDescription ? (
          <>
            <Button
              color="secondary"
              onClick={handleClose}
              disabled={isProcessing}
              className="mr-2"
            >
              Cancelar
            </Button>
            <Button
              color="primary"
              onClick={handleGenerateAI}
              disabled={
                isProcessing ||
                selectedUrls.length < 2 ||
                selectedUrls.length > 5
              }
            >
              {isProcessing ? (
                <>
                  <Spinner size="sm" className="mr-2" /> Generando...
                </>
              ) : (
                <>
                  <i className="fa fa-duotone fa-sparkles mr-2" /> Generar con IA
                </>
              )}
            </Button>
          </>
        ) : (
          <>
            <Button
              color="secondary"
              className="mr-2"
              onClick={() => setGeneratedDescription('')}
            >
              Volver a intentar
            </Button>
            <Button color="success" onClick={handleApplyDescription}>
              <i className="fa fa-check mr-2" /> Aplicar a la propiedad
            </Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
};

export default GenerateDescriptionAIModal;
